import type { TransportTestSenderConfig } from '@/config';
import type { PixelEvent } from '@/events';
import type { PixelLogger } from '@/logger';

import type { EventSender, SendOptions, SenderDebugState } from './types';

export type TestSenderOptions = {
  readonly capture?: boolean;
  readonly response?: 'success' | 'failure';
  readonly delayMs?: number;
  readonly failUntilAttempt?: number;
  readonly simulateTimeout?: boolean;
};

type MutableTestSenderOptions = {
  -readonly [Key in keyof TestSenderOptions]: TestSenderOptions[Key];
};

export class TestSender implements EventSender {
  private readonly logger: PixelLogger;

  private readonly options: MutableTestSenderOptions;

  private readonly sentBatches: PixelEvent[][] = [];

  private sendAttempts = 0;

  private sentCount = 0;

  private lastAck: boolean | null = null;

  constructor(logger: PixelLogger, options: TestSenderOptions = {}) {
    this.logger = logger;
    this.options = {
      capture: options.capture ?? true,
      response: options.response ?? 'success',
      delayMs: normalizeDelay(options.delayMs),
      failUntilAttempt: options.failUntilAttempt,
      simulateTimeout: options.simulateTimeout,
    };
  }

  static fromConfig(
    logger: PixelLogger,
    config: TransportTestSenderConfig
  ): TestSender {
    return new TestSender(logger, config);
  }

  async send(
    events: PixelEvent[],
    options?: SendOptions
  ): Promise<{ ack: boolean; eventCount: number }> {
    if (events.length === 0) return { ack: true, eventCount: 0 };
    throwIfAborted(options?.signal);

    this.sendAttempts += 1;
    if (
      this.options.failUntilAttempt !== undefined &&
      this.sendAttempts < this.options.failUntilAttempt
    ) {
      this.lastAck = false;
      throw new Error(
        `[TestSender] Simulated failure (attempt ${this.sendAttempts})`
      );
    }

    if (this.options.simulateTimeout) {
      this.lastAck = false;
      throw new DOMException('The operation was aborted.', 'AbortError');
    }

    await this.waitWithAbort(options?.signal);

    if (this.options.response === 'failure') {
      this.lastAck = false;
      throw new Error('[TestSender] Simulated failure response');
    }

    if (this.options.capture) this.sentBatches.push([...events]);
    this.sentCount += events.length;
    this.lastAck = true;
    this.logger.debug(
      `[TestSender] Simulated send of ${events.length} event(s)`
    );
    return { ack: true, eventCount: events.length };
  }

  getSentBatches(): readonly (readonly PixelEvent[])[] {
    return this.sentBatches.map(batch => [...batch]);
  }

  getSentCount(): number {
    return this.sentCount;
  }

  getLogs(): readonly string[] {
    return this.sentBatches.map(
      batch => `[TestSender] ${batch.map(event => event.type).join(', ')}`
    );
  }

  getState(): SenderDebugState & {
    readonly mode: 'test';
    readonly capturedBatches: readonly (readonly PixelEvent[])[];
    readonly attempts: number;
  } {
    return {
      sentCount: this.getSentCount(),
      logs: this.getLogs(),
      lastAck: this.lastAck,
      mode: 'test',
      capturedBatches: this.getSentBatches(),
      attempts: this.sendAttempts,
    };
  }

  clear(): void {
    this.sentBatches.length = 0;
    this.sendAttempts = 0;
    this.sentCount = 0;
    this.lastAck = null;
  }

  /** @internal Playground and resilience tests only. */
  configure(options: Partial<TestSenderOptions>): void {
    if (options.capture !== undefined) this.options.capture = options.capture;
    if (options.response !== undefined)
      this.options.response = options.response;
    if (options.delayMs !== undefined) {
      this.options.delayMs = normalizeDelay(options.delayMs);
    }
    if ('failUntilAttempt' in options) {
      this.options.failUntilAttempt = options.failUntilAttempt;
    }
    if (options.simulateTimeout !== undefined) {
      this.options.simulateTimeout = options.simulateTimeout;
    }
  }

  private waitWithAbort(signal?: AbortSignal): Promise<void> {
    const delayMs = this.options.delayMs ?? 0;
    if (delayMs === 0) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        resolve();
      }, delayMs);
      const onAbort = (): void => {
        cleanup();
        reject(new DOMException('The operation was aborted.', 'AbortError'));
      };
      const cleanup = (): void => {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
      };

      if (signal?.aborted) return onAbort();
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  }
}

function normalizeDelay(delayMs: number | undefined): number {
  return Math.max(0, delayMs ?? 0);
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }
}
